<template>
  <div :class="['alert', metricName]">
    <div class="alert__title" data-test="alert_state_duration_title">
      {{ $t('amd:alerts_configuration_title', { metricName: name }) }}
    </div>
    <div :class="['alert__settings', {'with-scrollbar': showScrollbar}]">
      <alert-state-duration-item v-for="(durationItem, index) in durationItemList"
                                 :ref="index"
                                 :key="durationItem.id"
                                 :index="index"
                                 :durationItem="durationItem"
                                 :validation="validation"
                                 @onInputChange="onInputChange"
                                 @clearItemClicked="onClearItemClick"
                                 @removeItemClicked="onRemoveItemClick"
                                 :data-test="`alert_state_duration_${durationItem.id}`"
                                 >
      </alert-state-duration-item>
    </div>
    <le-button v-show="showAddButton" class="alert__add"
               variant="link"
               :label="`${$t('amd:alerts_configuration_add')}`"
               @click="onAddDurationItemClick"
               data-test="alert_state_duration_add_btn"
               >
    </le-button>
  </div>
</template>

<script>
/* eslint-disable max-len, no-unused-expressions */
import { isEmpty, each, filter, clone, every } from 'lodash';
import { leSelect, leButton } from 'lp-vue-components';
import AlertStateDurationItem from './alert-state-duration-item';

const STUB_CONDITION_TYPE_STATE_DURATION = 'stateDuration';
const SCROLLBAR_THRESHOLD_VALUE = 6;

export default {
  name: 'alert-state-duration',
  components: {
    AlertStateDurationItem,
    leSelect,
    leButton,
  },
  props: {
    metricName: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    values: {
      type: Object,
      default: undefined,
    },
    validation: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      durationItemList: this.getInitialData(),
      showAddButton: true,
      changeEventEmitted: false,
    };
  },
  computed: {
    showScrollbar() {
      return this.durationItemList.length > SCROLLBAR_THRESHOLD_VALUE;
    },
  },
  methods: {
    getInitialData() {
      return this.values &&
      this.values.durationItems &&
      this.values.durationItems.length ? clone(this.values.durationItems) : [{ id: 0 }];
    },
    onConditionTypeChange(conditionType) {
      this.emitChanges('conditionType', conditionType.value);
    },
    onAddDurationItemClick() {
      this.durationItemList.push({
        id: this.durationItemList[this.durationItemList.length - 1].id + 1 },
      );
    },
    onClearItemClick() {
      this.emitChanges();
    },
    onRemoveItemClick(index) {
      if (this.durationItemList.length > 1) {
        this.durationItemList.splice(index, 1);
        this.emitChanges();
      }
    },
    onInputChange() {
      this.emitChanges();
    },
    emitChanges() {
      if (!this.changeEventEmitted) {
        this.$emit('onChange');
        this.changeEventEmitted = true;
      }
    },
    validateChildComponents() {
      const validationResults = [];
      each(filter(this.$refs, ref => !isEmpty(ref)), (ref) => {
        validationResults.push(ref[0].validate());
      });
      return every(validationResults, item => item);
    },
    validate() {
      let validationResult = true;
      this.resetErrorsInChildComponents();
      validationResult = this.validateChildComponents();
      const inputData = validationResult ? {} : this.getAlertData();
      return { ...inputData, isValid: validationResult };
    },
    getValuesFromRefs() {
      const stateDurationValues = [];
      each(filter(this.$refs, ref => !isEmpty(ref)), (ref) => {
        const stateDurationData = ref[0].getChildData();
        if (stateDurationData) {
          stateDurationValues.push(stateDurationData);
        }
      });
      return stateDurationValues;
    },
    getAlertData() {
      const durationItems = this.getValuesFromRefs();
      return {
        metricName: this.metricName,
        // ConditionType here is hardcoded since there might be
        // multiple childComponents each with it's own conditionType.
        conditionType: STUB_CONDITION_TYPE_STATE_DURATION,
        values: {
          durationItems,
        },
      };
    },
    reset() {
      this.$emit('onClear', this.getAlertData());
      each(filter(this.$refs, ref => !isEmpty(ref)), (ref) => {
        ref[0].resetInputs();
      });
      this.durationItemList = [];
    },
    resetErrorsInChildComponents() {
      each(this.$refs, (ref) => {
        !isEmpty(ref) && ref[0].clearError();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.alert {
  margin: 5px 0 10px 0;
  .label {
    @include alert-dialog-label();
  }
  &__title {
    font-weight: bold;
    margin-bottom: 10px;
  }
  .with-scrollbar {
    max-height: 500px;
    overflow-y: scroll;
  }
}
</style>
